import { useEffect } from "react";
import { Mail, Phone, MapPin } from "lucide-react";
import {
  privacySections,
  LAST_UPDATED,
  COMPANY,
} from "./privacy.data";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
    });
  };

  return (
    <section className="relative overflow-hidden pt-6 pb-20">
      {/* Background decoration */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[420px] w-[420px] rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute bottom-0 right-10 h-[300px] w-[300px] rounded-full bg-accent/20 blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto px-6 sm:px-12 flex flex-col gap-12">

        {/* Header */}
        <div className="text-center">
          <h1 className="text-hero tracking-tight text-primary">
            Privacy Policy
          </h1>

          <p className="mt-4 text-body text-gray-700">
            How {COMPANY.name} collects, uses and protects your personal data.
          </p>

          <p className="mt-2 text-sm text-gray-500">
            Last updated: {LAST_UPDATED}
          </p>
        </div>

        {/* Quick navigation */}
        <nav
          aria-label="Privacy policy sections"
          className="bg-white/70 border border-primary/20 rounded-xl p-6"
        >
          <h2 className="text-sm font-semibold text-primary uppercase tracking-wide">
            On this page
          </h2>

          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
            {privacySections.map((section) => (
              <li key={section.id}>
                <button
                  type="button"
                  onClick={() => scrollToSection(section.id)}
                  className="text-left text-sm text-gray-700 hover:text-primary hover:underline transition"
                >
                  {section.title}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Sections */}
        <div className="flex flex-col gap-10">
          {privacySections.map((section) => (
            <article
              key={section.id}
              id={section.id}
              className="scroll-mt-24 flex flex-col gap-4"
            >
              <h2 className="text-subtitle font-semibold text-primary">
                {section.title}
              </h2>

              <div className="flex flex-col gap-4">
                {section.blocks.map((block, index) => {
                  if (block.type === "list") {
                    return (
                      <ul
                        key={index}
                        className="flex flex-col gap-2 pl-5 list-disc marker:text-primary/60"
                      >
                        {block.items.map((item, itemIndex) => (
                          <li
                            key={itemIndex}
                            className="text-body text-gray-700 leading-7"
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                    );
                  }

                  return (
                    <p
                      key={index}
                      className="text-body text-gray-700 leading-7"
                    >
                      {block.content}
                    </p>
                  );
                })}
              </div>
            </article>
          ))}
        </div>

        {/* Contact */}
        <div className="bg-white/70 border border-primary/20 rounded-xl p-6 flex flex-col gap-4">
          <h2 className="text-subtitle font-semibold text-primary">
            Questions about your data?
          </h2>

          <p className="text-body text-gray-700">
            Write to us and we will get back to you.
          </p>

          <div className="flex flex-col gap-3 text-body text-gray-700">

            <a
              href={`mailto:${COMPANY.email}`}
              className="flex items-start gap-3 hover:text-primary transition"
            >
              <Mail size={16} className="mt-1 shrink-0" />
              <span>{COMPANY.email}</span>
            </a>

            <a
              href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
              className="flex items-start gap-3 hover:text-primary transition"
            >
              <Phone size={16} className="mt-1 shrink-0" />
              <span>{COMPANY.phone}</span>
            </a>

            <a
              href={`https://www.google.com/maps?q=${encodeURIComponent(
                COMPANY.address
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 hover:text-primary transition"
            >
              <MapPin size={16} className="mt-1 shrink-0" />
              <span>{COMPANY.address}</span>
            </a>

          </div>
        </div>

      </div>
    </section>
  );
};

export default PrivacyPolicy;
